import React, { useState, useEffect, useCallback } from 'react';
import GestorLayout from '../../components/GestorLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Plus, Trash2, FolderOpen, GripVertical, Edit2, Save, X, ArrowUpDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GestaoCategorias() {
  const [categorias, setCategorias] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ nome: '', codigo: '', descricao: '' });
  const [draggedItem, setDraggedItem] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    codigo: '',
    descricao: ''
  });

  useEffect(() => {
    loadCategorias();
  }, []);

  const loadCategorias = async () => {
    try {
      const response = await axios.get(`${API}/categorias`);
      setCategorias(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome) {
      toast.error('Preencha o nome da categoria');
      return;
    }
    if (!formData.codigo) {
      toast.error('Preencha o código da categoria (ex: INT, ACA)');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/categorias`, {
        nome: formData.nome,
        codigo: formData.codigo.toUpperCase(),
        descricao: formData.descricao
      });

      toast.success('Categoria criada com sucesso!');
      setShowDialog(false);
      setFormData({ nome: '', codigo: '', descricao: '' });
      loadCategorias();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Categoria já existe');
      } else {
        toast.error('Erro ao criar categoria');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (categoriaId, categoriaNome) => {
    if (!window.confirm(`Tem a certeza que deseja eliminar a categoria "${categoriaNome}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/categorias/${categoriaId}`);
      toast.success('Categoria eliminada');
      loadCategorias();
    } catch (error) {
      toast.error('Erro ao eliminar categoria');
    }
  };

  const handleStartEdit = (categoria) => {
    setEditingId(categoria.id);
    setEditForm({ nome: categoria.nome, codigo: categoria.codigo || '', descricao: categoria.descricao || '' });
  };

  const handleSaveEdit = async (categoriaId) => {
    if (!editForm.nome) {
      toast.error('O nome é obrigatório');
      return;
    }
    if (!editForm.codigo) {
      toast.error('O código é obrigatório');
      return;
    }

    try {
      await axios.put(`${API}/categorias/${categoriaId}`, {
        nome: editForm.nome,
        codigo: editForm.codigo.toUpperCase(),
        descricao: editForm.descricao,
        ordem: categorias.find(c => c.id === categoriaId)?.ordem || 0
      });
      toast.success('Categoria atualizada');
      setEditingId(null);
      loadCategorias();
    } catch (error) {
      toast.error('Erro ao atualizar categoria');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ nome: '', codigo: '', descricao: '' });
  };

  // Drag & Drop handlers
  const handleDragStart = (e, categoria) => {
    setDraggedItem(categoria);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetCategoria) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetCategoria.id) {
      setDraggedItem(null);
      return;
    }

    // Reordenar localmente
    const newCategorias = [...categorias];
    const draggedIndex = newCategorias.findIndex(c => c.id === draggedItem.id);
    const targetIndex = newCategorias.findIndex(c => c.id === targetCategoria.id);
    
    newCategorias.splice(draggedIndex, 1);
    newCategorias.splice(targetIndex, 0, draggedItem);
    
    // Atualizar ordem
    const ordens = newCategorias.map((cat, idx) => ({
      id: cat.id,
      ordem: idx + 1
    }));

    setCategorias(newCategorias.map((cat, idx) => ({ ...cat, ordem: idx + 1 })));
    setDraggedItem(null);

    // Salvar no backend
    try {
      await axios.post(`${API}/categorias/reordenar`, ordens);
      toast.success('Ordem das categorias atualizada');
    } catch (error) {
      console.error('Erro ao reordenar:', error);
      toast.error('Erro ao salvar a nova ordem');
      loadCategorias(); // Recarregar em caso de erro
    }
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  return (
    <GestorLayout>
      <div className="px-8 pt-4 pb-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-stone-900 mb-2">Categorias</h1>
            <p className="text-stone-600">Gerir categorias de operações. Arraste para reordenar.</p>
          </div>
          <Button
            onClick={() => setShowDialog(true)}
            className="rounded-full"
            data-testid="criar-categoria-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Categoria
          </Button>
        </div>

        {/* Nota sobre ordenação */}
        <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-3">
            <ArrowUpDown className="h-5 w-5 text-blue-600" />
            <p className="text-sm text-blue-800">
              A ordem das categorias define como as operações aparecem em toda a aplicação. 
              Arraste os cartões para reorganizar.
            </p>
          </div>
        </Card>

        {/* Lista de Categorias */}
        <div className="space-y-3">
          {categorias.map((categoria, index) => (
            <Card 
              key={categoria.id} 
              className={`p-4 transition-all cursor-move ${
                draggedItem?.id === categoria.id ? 'opacity-50 scale-95' : ''
              } ${editingId === categoria.id ? 'ring-2 ring-primary' : ''}`}
              data-testid={`categoria-card-${categoria.nome}`}
              draggable={editingId !== categoria.id}
              onDragStart={(e) => handleDragStart(e, categoria)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, categoria)}
              onDragEnd={handleDragEnd}
            >
              <div className="flex items-center gap-4">
                {/* Handle de arrastar */}
                <div className="flex items-center gap-2 text-stone-400">
                  <GripVertical className="h-5 w-5" />
                  <span className="w-6 h-6 rounded-full bg-stone-100 flex items-center justify-center text-xs font-semibold">
                    {index + 1}
                  </span>
                </div>

                {/* Ícone da categoria */}
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <FolderOpen className="h-5 w-5 text-primary" />
                </div>

                {/* Conteúdo */}
                <div className="flex-1 min-w-0">
                  {editingId === categoria.id ? (
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        value={editForm.nome}
                        onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
                        placeholder="Nome da categoria"
                        autoFocus
                      />
                      <Input
                        value={editForm.codigo}
                        onChange={(e) => setEditForm({ ...editForm, codigo: e.target.value.toUpperCase() })}
                        placeholder="Código (ex: INT)"
                        maxLength={4}
                        className="font-mono uppercase"
                      />
                      <Input
                        value={editForm.descricao}
                        onChange={(e) => setEditForm({ ...editForm, descricao: e.target.value })}
                        placeholder="Descrição (opcional)"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-heading font-bold text-stone-900 capitalize">
                          {categoria.nome}
                        </h3>
                        {categoria.codigo && (
                          <span className="text-xs font-mono px-2 py-0.5 bg-navy/10 text-navy rounded">
                            {categoria.codigo}
                          </span>
                        )}
                      </div>
                      {categoria.descricao && (
                        <p className="text-sm text-stone-500">{categoria.descricao}</p>
                      )}
                    </>
                  )}
                </div>

                {/* Ações */}
                <div className="flex items-center gap-1">
                  {editingId === categoria.id ? (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleSaveEdit(categoria.id)}
                        className="text-success hover:bg-success/10"
                      >
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleCancelEdit}
                        className="text-stone-500 hover:bg-stone-100"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleStartEdit(categoria)}
                        className="text-stone-500 hover:bg-stone-100"
                        data-testid={`edit-categoria-${categoria.nome}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(categoria.id, categoria.nome)}
                        className="text-error hover:bg-error/10"
                        data-testid={`delete-categoria-${categoria.nome}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {categorias.length === 0 && (
          <Card className="p-12 text-center">
            <FolderOpen className="h-16 w-16 mx-auto text-stone-300 mb-4" />
            <p className="text-stone-500">Nenhuma categoria cadastrada</p>
          </Card>
        )}
      </div>

      {/* Dialog Criar Categoria */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="criar-categoria-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Nova Categoria</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Nome *</label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                placeholder="ex: Intermédios"
                data-testid="nome-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Código * <span className="text-xs text-stone-400">(usado nos lotes, ex: INT, ACA)</span>
              </label>
              <Input
                value={formData.codigo}
                onChange={(e) => setFormData({...formData, codigo: e.target.value.toUpperCase()})}
                placeholder="ex: INT"
                maxLength={4}
                className="font-mono uppercase w-32"
                data-testid="codigo-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Descrição</label>
              <Input
                value={formData.descricao}
                onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                placeholder="Descrição da categoria"
                data-testid="descricao-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setShowDialog(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 rounded-full"
                disabled={loading}
                data-testid="submit-categoria-button"
              >
                {loading ? 'A criar...' : 'Criar Categoria'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </GestorLayout>
  );
}
